import { Dictionary } from 'lodash';
import { keyBy, lowerCase } from 'lodash/fp';
import { FC, useMemo } from 'react';

import NavBack from 'dashboard/components/NavBack';
import { TabbedSection } from 'dashboard/components/TabbedSectionV2';
import { Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { Container } from 'dashboard/layouts/ShowViewLayoutV2';
import {
  ConnectionNode,
  PlatformConfigType,
  VersionLevelConnectionNode,
  WorkflowNode,
} from 'dashboard/models/platform-node';
import { NODE_TYPE_TO_LABEL } from 'dashboard/views/DashboardPlatformConnectionsShowView/constants';
import { routeToNode } from 'dashboard/views/DashboardPlatformConnectionsShowView/PlatformNodeTypes/PlatformNodeLink';
import TableConnectionNode from 'dashboard/views/DashboardPlatformConnectionsShowView/PlatformNodeTypes/TableConnectionNode';
import TableWorkflowNode from 'dashboard/views/DashboardPlatformConnectionsShowView/PlatformNodeTypes/TableWorkflowNode';
import Badge from 'design-system/components/Badge';
import { ApiModel } from 'lib/interfaces/api';

const isWorkflowNode = (node: ConnectionNode): node is WorkflowNode => {
  return node.type === PlatformConfigType.Workflow;
};

const isVersionLevelNode = (node: ConnectionNode): node is VersionLevelConnectionNode => {
  return [
    PlatformConfigType.InquiryTemplateVersion,
    PlatformConfigType.ReportTemplateVersion,
    PlatformConfigType.VerificationTemplateVersion,
    PlatformConfigType.WorkflowVersion,
  ].includes(node.type);
};

const filterDirectWorkflows = (nodes: ConnectionNode[]) => {
  return nodes.filter((node) =>
    isWorkflowNode(node) ? node.metadata?.['trigger-type'] !== ApiModel.TriggerDirect : true,
  );
};

export type PlatformConnectionsDirection = 'outbound' | 'inbound';

interface DashboardPlatformConnectionsShowViewProps {
  inboundConnections: ConnectionNode[];
  outboundConnections: ConnectionNode[];
  rootNode: ConnectionNode;
}

const DashboardPlatformConnectionsShowView: FC<DashboardPlatformConnectionsShowViewProps> = ({
  rootNode,
  inboundConnections,
  outboundConnections,
}) => {
  const nodeTokenToNode = useMemo(
    () => keyBy('token', [...inboundConnections, ...outboundConnections, rootNode]),
    [inboundConnections, outboundConnections, rootNode],
  );

  const linkToNode = routeToNode(rootNode);
  const outboundConnectionNodes = filterDirectWorkflows(outboundConnections);
  const inboundConnectionNodes = filterDirectWorkflows(inboundConnections);

  const rootNodeIsLatestPublishedVersion =
    isVersionLevelNode(rootNode) && rootNode.metadata['is-latest-published-version'];

  return (
    <Container>
      <Header
        title={`Platform Connections: ${rootNode.metadata?.name} (${NODE_TYPE_TO_LABEL[rootNode.type]})`}
        prepend={linkToNode && <NavBack to={linkToNode} />}
      />
      <Section>
        <TabbedSection
          cardView={true}
          tabs={[
            {
              name: 'Creates',
              contents: (
                <ConnectionsTable
                  rootNodeType={rootNode.type}
                  connections={outboundConnectionNodes}
                  connectionsDirection='outbound'
                  nodeTokenToNode={nodeTokenToNode}
                  rootNodeIsLatestPublishedVersion={rootNodeIsLatestPublishedVersion}
                />
              ),
              badge: <Badge>{outboundConnectionNodes.length}</Badge>,
            },
            {
              name: 'Created By',
              contents: (
                <ConnectionsTable
                  rootNodeType={rootNode.type}
                  connections={inboundConnectionNodes}
                  connectionsDirection='inbound'
                  nodeTokenToNode={nodeTokenToNode}
                  rootNodeIsLatestPublishedVersion={rootNodeIsLatestPublishedVersion}
                />
              ),
              badge: <Badge>{inboundConnectionNodes.length}</Badge>,
            },
          ]}
        />
      </Section>
    </Container>
  );
};

interface ConnectionsTableProps {
  connections: ConnectionNode[];
  rootNodeType: PlatformConfigType;
  rootNodeIsLatestPublishedVersion?: boolean;
  nodeTokenToNode: Dictionary<ConnectionNode>;
  connectionsDirection: PlatformConnectionsDirection;
}

const ConnectionsTable: FC<ConnectionsTableProps> = ({
  connections,
  rootNodeType,
  rootNodeIsLatestPublishedVersion,
  nodeTokenToNode,
  connectionsDirection,
}) => {
  // Workflow Display Criteria:
  // If the root node is a workflow, all connections of depth 1 should be shown as direct
  // connections for both upstream and downstream
  //
  // Non-Workflow Display Criteria:
  // Downstream:
  // direct connecitions - all objects, excluding workflows, with a depth of 1
  // workflow nodes - all workflows with a depth of 1
  // downstream connections - all objects, excluding workflows, with a depth of 2
  //
  // Upstream:
  // direct connections - all objects with a depth of 1
  const rootIsWorkflow = rootNodeType === PlatformConfigType.WorkflowVersion;
  const workflowNodes = connections.filter(
    (node): node is WorkflowNode => node.depth === 1 && isWorkflowNode(node),
  );
  const directNodes =
    connectionsDirection === 'inbound' || rootIsWorkflow
      ? connections.filter((node) => node.depth === 1)
      : connections.filter((node) => node.depth === 1 && !isWorkflowNode(node));
  const downstreamNodes = connections.filter((node) => node.depth === 2);

  return (
    <>
      {(connectionsDirection === 'inbound' ||
        [PlatformConfigType.WorkflowVersion, PlatformConfigType.InquiryTemplateVersion].includes(
          rootNodeType,
        )) && (
        <TableConnectionNode
          tableName='Direct Connections'
          description={
            connectionsDirection === 'outbound'
              ? `The following can be created within this ${lowerCase(NODE_TYPE_TO_LABEL[rootNodeType])}`
              : `This ${lowerCase(NODE_TYPE_TO_LABEL[rootNodeType])} is ${rootIsWorkflow ? 'triggered or ' : ''}referenced by the following objects`
          }
          displayConnectedBy={false}
          nodeTokenToNode={nodeTokenToNode}
          rootNodeIsLatestPublishedVersion={rootNodeIsLatestPublishedVersion}
          connectionsDirection={connectionsDirection}
          nodes={directNodes}
        />
      )}
      {connectionsDirection === 'outbound' && !rootIsWorkflow && (
        <>
          <TableConnectionNode
            tableName='Downstream Connections'
            description='The following can be created as the result of a workflow or action'
            nodeTokenToNode={nodeTokenToNode}
            connectionsDirection={connectionsDirection}
            rootNodeIsLatestPublishedVersion={rootNodeIsLatestPublishedVersion}
            displayConnectedBy={true}
            nodes={downstreamNodes}
          />
          <TableWorkflowNode nodes={workflowNodes} rootNodeType={rootNodeType} />
        </>
      )}
    </>
  );
};

export default DashboardPlatformConnectionsShowView;
